/* **** THIS COMPONENT AND ITS USAGE WILL BE REPLACED WITH THE NEW ChipWithCustomTooltip **** */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { DataTestID, lang, useUserRole } from 'src/utils';

import { CustomTooltip } from '../tooltip/CustomTooltip';
import { IDynamicTooltipChip } from './DynamicTooltipChip.interface';
import { StyledChip } from './DynamicTooltipChip.styled';

export const DynamicTooltipChip = ({
  className,
  tooltipTitle,
  tooltipWidth,
  tooltipPlacement,
  tooltipActivationWidth,
  chipName,
  color,
  variant,
  size,
  onDelete,
  onClick,
  replaceSpaces = true,
  removeBackslashes = false,
  name,
  isMatched = true,
  containsEscapedBackslash,
  forceTooltip = false,
  ...rest
}: IDynamicTooltipChip) => {
  const ref = useRef<HTMLDivElement>();
  const [overflow, setOverflow] = useState(false);
  const [clientWidth, setClientWidth] = useState(0);
  const { isViewOnly } = useUserRole();
  const formatText = (
    text: string,
    replaceSpaces: boolean,
    removeBackslashes: boolean,
  ) => {
    let result = text;

    if (replaceSpaces) {
      result = result?.replace(/ /g, '\u00a0');
    }
    if (removeBackslashes) {
      result = result?.replace(/\\+/g, (match) => {
        if (match.length % 2 !== 0) {
          return '';
        }

        return match;
      });
    }
    if (containsEscapedBackslash) {
      result = result.replaceAll('\\\\', '\\');
    }

    return result;
  };

  const relevantNames = [
    lang.componentTags,
    lang.assemblyTags,
    lang.componentTag,
    lang.locationTags,
    lang.assemblyTag,
    lang.addAssemblyTag,
    lang.addTag,
  ];

  useEffect(() => {
    if (name && relevantNames.includes(name)) {
      if (
        ref.current &&
        clientWidth > 0 &&
        clientWidth >= ref.current.clientWidth &&
        tooltipActivationWidth &&
        chipName
      ) {
        if (
          ref.current.clientWidth < tooltipActivationWidth &&
          clientWidth > ref.current.clientWidth
        )
          setOverflow(ref.current.clientWidth > tooltipActivationWidth);
        else setOverflow(clientWidth > tooltipActivationWidth);
      } else if (ref.current && tooltipActivationWidth && chipName) {
        setClientWidth(ref.current.clientWidth);
        setOverflow(ref.current.clientWidth > tooltipActivationWidth);
      }

      if (
        ref.current &&
        tooltipActivationWidth &&
        chipName &&
        isViewOnly &&
        name === lang.addTag
      ) {
        setOverflow(ref.current.clientWidth === tooltipActivationWidth);
      }
    } else {
      if (ref.current && tooltipActivationWidth && chipName)
        setOverflow(ref.current.scrollWidth > tooltipActivationWidth);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltipActivationWidth, chipName, clientWidth, name]);

  return overflow || forceTooltip ? (
    <CustomTooltip
      width={tooltipWidth}
      placement={tooltipPlacement || 'bottom'}
      title={formatText(
        tooltipTitle as string,
        replaceSpaces,
        removeBackslashes,
      )}
      skipValidateOverflow={true}>
      <StyledChip
        className={className}
        data-testid={DataTestID.CustomChip}
        ref={ref}
        variant={variant}
        $overflow={overflow}
        $tooltipActivationWidth={tooltipActivationWidth}
        label={formatText(chipName as string, replaceSpaces, removeBackslashes)}
        color={color}
        size={size}
        {...(onDelete && { onDelete: () => onDelete(chipName) })}
        {...(onClick && {
          onClick: (event: MouseEvent<HTMLDivElement>) => onClick(event),
        })}
        $isNotMatched={!isMatched}
        {...(!isMatched && { icon: <WarningAmberIcon fontSize="small" /> })}
        {...rest}
      />
    </CustomTooltip>
  ) : (
    <StyledChip
      $tooltipActivationWidth={tooltipActivationWidth}
      ref={ref}
      className={className}
      data-testid={DataTestID.CustomChip}
      label={formatText(chipName as string, replaceSpaces, removeBackslashes)}
      variant={variant}
      color={color}
      size={size}
      {...(onDelete && { onDelete: () => onDelete(chipName) })}
      {...(onClick && {
        onClick: (event: MouseEvent<HTMLDivElement>) => onClick(event),
      })}
      $isNotMatched={!isMatched}
      {...(!isMatched && { icon: <WarningAmberIcon fontSize="small" /> })}
      {...rest}
    />
  );
};
